import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { RELEASES_PLACEHOLDER } from "../lib/assets";
import { resolveUploadUrl } from "../components/ImageUploadField";

/** Render an audio embed from a URL.
 *  - YouTube/Spotify/Bandcamp/SoundCloud → iframe
 *  - direct .mp3/.wav/.ogg → <audio>
 *  - else: link
 */
function AudioEmbed({ url }) {
  if (!url) return null;
  const u = url.toLowerCase();

  // YouTube
  if (u.includes("youtube.com") || u.includes("youtu.be")) {
    let id = "";
    try {
      const parsed = new URL(url);
      if (parsed.hostname.includes("youtu.be")) id = parsed.pathname.slice(1);
      else id = parsed.searchParams.get("v") || "";
    } catch (_e) {
      // ignore parse errors
    }
    if (id) {
      return (
        <iframe
          title="YouTube player"
          className="w-full aspect-video border border-[color:var(--nccc-border)]"
          src={`https://www.youtube.com/embed/${id}`}
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
        />
      );
    }
  }
  // Bandcamp
  if (u.includes("bandcamp.com/EmbeddedPlayer".toLowerCase()) || u.includes("bandcamp.com")) {
    return (
      <iframe
        title="Bandcamp player"
        className="w-full border border-[color:var(--nccc-border)]"
        style={{ height: 120 }}
        src={url}
      />
    );
  }
  // SoundCloud
  if (u.includes("soundcloud.com")) {
    return (
      <iframe
        title="SoundCloud player"
        className="w-full border border-[color:var(--nccc-border)]"
        style={{ height: 160 }}
        src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&color=%23d4a373&inverse=true`}
      />
    );
  }
  // Direct audio file
  if (/\.(mp3|wav|ogg|m4a|flac)(\?|$)/i.test(u)) {
    return (
      <audio controls className="w-full" src={resolveUploadUrl(url)}>
        Your browser does not support the audio element.
      </audio>
    );
  }
  // Fallback link
  return (
    <a href={url} target="_blank" rel="noreferrer" className="nccc-btn">
      Open audio ↗
    </a>
  );
}

export default function ReleaseDetail() {
  const { catalogue } = useParams();
  const [release, setRelease] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/releases/by-catalogue/${catalogue}`)
      .then((r) => setRelease(r.data))
      .catch(() => setNotFound(true));
  }, [catalogue]);

  if (notFound) {
    return (
      <div className="relative z-10 max-w-3xl mx-auto px-6 lg:px-12 py-24 text-center" data-testid="release-not-found">
        <span className="label-accent">// Signal lost</span>
        <h1 className="broadcast-headline text-4xl mt-3 text-[color:var(--nccc-cream)]">
          Catalogue not found.
        </h1>
        <Link to="/releases" className="nccc-btn nccc-btn-ghost mt-8 inline-flex">
          <ArrowLeft size={14} /> Back to releases
        </Link>
      </div>
    );
  }

  if (!release) {
    return <p className="label p-12 relative z-10">// tuning catalogue...</p>;
  }

  const coverUrl = resolveUploadUrl(release.cover_url) || RELEASES_PLACEHOLDER;

  return (
    <div
      className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-24"
      data-testid="release-detail-page"
    >
      <Link
        to="/releases"
        className="label inline-flex items-center gap-2 mb-10 hover:text-[color:var(--nccc-ochre)]"
        data-testid="release-detail-back"
      >
        <ArrowLeft size={14} /> Catalogue
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 warm-up">
          <div
            className="relative aspect-square mx-auto max-w-[460px]"
            style={{
              clipPath:
                "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
              background: `linear-gradient(180deg, rgba(61,38,20,0.05) 0%, rgba(61,38,20,0.4) 100%), url(${coverUrl}) center/cover no-repeat`,
              filter: "sepia(0.25) contrast(1.02) saturate(1.05)",
            }}
            data-testid="release-detail-cover"
          />
        </div>

        <div className="lg:col-span-7 warm-up-2">
          <span className="label-accent">{release.catalogue}</span>
          <h1
            className="broadcast-headline text-4xl lg:text-6xl mt-3 chromatic text-[color:var(--nccc-cream)]"
            data-testid="release-detail-title"
          >
            {release.title}
          </h1>
          <p className="mt-3 label" data-testid="release-detail-artist">
            {release.artist}
            {release.format ? ` · ${release.format}` : ""}
            {release.year ? ` · ${release.year}` : ""}
          </p>

          {release.description && (
            <p className="mt-8 text-[color:var(--nccc-cream)] leading-relaxed">
              {release.description}
            </p>
          )}

          {release.audio_url && (
            <div className="mt-10" data-testid="release-detail-audio">
              <span className="label mb-3 block">// listen</span>
              <AudioEmbed url={release.audio_url} />
            </div>
          )}

          <div className="mt-10 flex flex-wrap gap-3">
            {release.listen_url && !release.audio_url && (
              <a
                href={release.listen_url}
                target="_blank"
                rel="noreferrer"
                className="nccc-btn"
                data-testid="release-detail-listen"
              >
                Listen ↗
              </a>
            )}
            {release.buy_url && (
              <a
                href={release.buy_url}
                target="_blank"
                rel="noreferrer"
                className="nccc-btn nccc-btn-ghost"
                data-testid="release-detail-buy"
              >
                Buy ↗
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
